/* global RequestInit */
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    let apiHostname: string;

    if (hostname === 'postrix.io') {
      apiHostname = 'backoffice-api.postrix.io';
    } else if (hostname.includes('backoffice')) {
      apiHostname = hostname.replace('backoffice', 'backoffice-api');
    } else {
      apiHostname = hostname;
    }

    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${apiHostname}${port}/api`;
  }
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function setAuthToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

export function clearAuthToken(): void {
  localStorage.removeItem('auth_token');
}

let globalLogoutHandler: (() => void) | null = null;

export function setLogoutHandler(handler: () => void): void {
  globalLogoutHandler = handler;
}

function handleAuthError(): void {
  clearAuthToken();
  if (globalLogoutHandler) {
    globalLogoutHandler();
  }
}

export async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.error || 'An error occurred', response.status);
  }

  return response.json();
}

export async function fetchFormDataWithAuth<T>(endpoint: string, formData: FormData): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(errorData.error || 'An error occurred', response.status);
  }

  return response.json();
}
